import React from "react";

/**
 * MoshkyMark — the Moshky (Velaro AI assistant) brand mark.
 *
 * The same three-path mark is currently inline-duplicated in CopilotAssistant.tsx (4x),
 * MainLayout.tsx (the FAB) and StarterWorkflowWizard.tsx. This is the extracted, shared
 * version — new surfaces import this instead of pasting a 7th copy. The path data is copied
 * verbatim from CopilotAssistant.tsx's panel-header instance (the canonical full version);
 * nothing about the artwork is new.
 *
 * Decorative by default (`aria-hidden`), because every current use pairs it with visible text
 * or a labelled button. Pass a `title` only when the mark is the sole content of an otherwise
 * unlabelled element.
 */
interface Props {
  /** Rendered pixel width. Height follows the 40:34 aspect ratio unless `height` is given. */
  width?: number;
  height?: number;
  /** Any valid SVG paint value — e.g. "white", "#374151", "currentColor". */
  fill?: string;
  /** Accessible name. When omitted the mark is aria-hidden (decorative). */
  title?: string;
  className?: string;
}

export default function MoshkyMark({
  width = 18,
  height,
  fill = "currentColor",
  title,
  className
}: Props) {
  const resolvedHeight = height ?? Math.round((width * 34) / 40);
  return (
    <svg
      width={width}
      height={resolvedHeight}
      viewBox="0 0 40 34"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      <path
        d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z"
        fill={fill}
      />
      <path
        d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z"
        fill={fill}
      />
      <path
        d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z"
        fill={fill}
      />
    </svg>
  );
}
